import { Head, Link, router } from "@inertiajs/react";
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Flex,
  Popover,
  Select,
  Spinner,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useState } from "react";

import { AppLayout } from "@/components/app-layout";
import type { SharedProps } from "@/lib/types";

type AdminUsersIndexProps = SharedProps & {
  users: Array<{
    id: number;
    email: string;
    harvest_name: string;
    harvest_user_id: string | null;
    bamboohr_user_id: string | null;
    bamboohr_display_name: string | null;
    recommended_bamboohr:
      | { id: string; display_name: string; strategy: string; confidence: number }
      | Record<string, never>;
    roles: string[];
    status: "active" | "archived";
    match_state: "mapped" | "unmapped" | "recommended";
  }>;
  roleValues: string[];
  filters: {
    harvest_name: string;
    match_state: string;
    role: string;
    status: string;
  };
  bamboohrOptions: Array<{ id: string; display_name: string }>;
  syncSummary?: {
    processed_count: number;
    new_count: number;
    archived_count: number;
    archived_users?: string[];
    skipped_count?: number;
  };
  newlyCreatedUserIds: number[];
  harvestSyncReady: boolean;
  harvestSyncReason: string | null;
};

function queryString(filters: AdminUsersIndexProps["filters"]) {
  const params = new URLSearchParams();
  if (filters.harvest_name) params.set("harvest_name", filters.harvest_name);
  if (filters.match_state) params.set("match_state", filters.match_state);
  if (filters.role) params.set("role", filters.role);
  if (filters.status) params.set("status", filters.status);
  return params.toString();
}

function roleLabel(role: string) {
  return role.replace("_", " ");
}

export default function AdminUsersIndex({
  auth,
  users,
  roleValues,
  filters: initialFilters,
  bamboohrOptions,
  syncSummary,
  newlyCreatedUserIds,
  harvestSyncReady,
  harvestSyncReason,
  flash,
}: AdminUsersIndexProps) {
  const [filters, setFilters] = useState(initialFilters);
  const [syncing, setSyncing] = useState(false);
  const [activeUserAction, setActiveUserAction] = useState<number | null>(null);

  function applyFilters() {
    router.get("/admin/users", filters, { preserveState: true });
  }

  function syncUsers() {
    if (!harvestSyncReady) return;

    const startedAt = Date.now();
    setSyncing(true);
    router.post(
      `/admin/users/sync${queryString(filters) ? `?${queryString(filters)}` : ""}`,
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, 500 - elapsed);
          window.setTimeout(() => setSyncing(false), remaining);
        },
      },
    );
  }

  function updateUser(
    userId: number,
    payload: { bamboohr_user_id?: string; bamboohr_display_name?: string; roles?: string[] },
  ) {
    setActiveUserAction(userId);
    router.patch(
      `/admin/users/${userId}${queryString(filters) ? `?${queryString(filters)}` : ""}`,
      {
        user: payload,
      },
      {
        preserveScroll: true,
        onFinish: () => setActiveUserAction(null),
      },
    );
  }

  function toggleRole(userId: number, existingRoles: string[], role: string) {
    const nextRoles = existingRoles.includes(role)
      ? existingRoles.filter((currentRole) => currentRole !== role)
      : [...existingRoles, role];

    updateUser(userId, { roles: nextRoles });
  }

  function mappedDisplayName(user: AdminUsersIndexProps["users"][number]) {
    return (
      bamboohrOptions.find((option) => option.id === user.bamboohr_user_id)?.display_name ||
      user.bamboohr_display_name ||
      ""
    );
  }

  return (
    <>
      <Head title="User Management" />
      <AppLayout
        user={auth.user}
        activeModule="weekly_planner"
        onModuleChange={() => {}}
        pageTitle="User Management"
        contextControls={
          <Flex gap="3" wrap="wrap" align="end">
            <div>
              <Text as="label" size="2" weight="medium">
                Harvest name
              </Text>
              <TextField.Root
                mt="1"
                value={filters.harvest_name}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, harvest_name: event.target.value }))
                }
                placeholder="Search name"
              />
            </div>
            <div>
              <Text as="label" size="2" weight="medium">
                Match state
              </Text>
              <Select.Root
                value={filters.match_state || "__all__"}
                onValueChange={(value) =>
                  setFilters((current) => ({
                    ...current,
                    match_state: value === "__all__" ? "" : value,
                  }))
                }
              >
                <Select.Trigger mt="1" />
                <Select.Content>
                  <Select.Item value="__all__">All</Select.Item>
                  <Select.Item value="mapped">Mapped</Select.Item>
                  <Select.Item value="recommended">Recommended</Select.Item>
                  <Select.Item value="unmapped">Unmapped</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>
            <div>
              <Text as="label" size="2" weight="medium">
                Role
              </Text>
              <Select.Root
                value={filters.role || "__all__"}
                onValueChange={(value) =>
                  setFilters((current) => ({ ...current, role: value === "__all__" ? "" : value }))
                }
              >
                <Select.Trigger mt="1" />
                <Select.Content>
                  <Select.Item value="__all__">All</Select.Item>
                  {roleValues.map((role) => (
                    <Select.Item key={role} value={role}>
                      {roleLabel(role)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </div>
            <div>
              <Text as="label" size="2" weight="medium">
                Status
              </Text>
              <Select.Root
                value={filters.status || "__all__"}
                onValueChange={(value) =>
                  setFilters((current) => ({
                    ...current,
                    status: value === "__all__" ? "" : value,
                  }))
                }
              >
                <Select.Trigger mt="1" />
                <Select.Content>
                  <Select.Item value="__all__">All</Select.Item>
                  <Select.Item value="active">Active</Select.Item>
                  <Select.Item value="archived">Archived</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>
            <Button variant="soft" onClick={applyFilters}>
              Apply filters
            </Button>
          </Flex>
        }
        pageActions={
          <Flex gap="2">
            <Link href="/">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
            <Button onClick={syncUsers} disabled={syncing || !harvestSyncReady}>
              {syncing ? (
                <>
                  <Spinner size="1" className="mr-2" />
                  Syncing...
                </>
              ) : (
                "Sync Users"
              )}
            </Button>
          </Flex>
        }
      >
        <div className="space-y-4">
          {flash.notice && (
            <Callout.Root color="green" variant="soft">
              <Callout.Text>{flash.notice}</Callout.Text>
            </Callout.Root>
          )}
          {flash.alert && (
            <Callout.Root color="red" variant="soft">
              <Callout.Text>{flash.alert}</Callout.Text>
            </Callout.Root>
          )}

          {!harvestSyncReady && harvestSyncReason && (
            <Callout.Root color="amber" variant="soft">
              <Callout.Text>{harvestSyncReason}</Callout.Text>
            </Callout.Root>
          )}

          {syncSummary && (
            <div className="space-y-2">
              <Flex gap="4" wrap="wrap">
                <Badge color="blue">Processed: {syncSummary.processed_count}</Badge>
                <Badge color="green">New: {syncSummary.new_count}</Badge>
                <Badge color="orange">Archived: {syncSummary.archived_count}</Badge>
                {!!syncSummary.skipped_count && (
                  <Badge color="amber">Skipped: {syncSummary.skipped_count}</Badge>
                )}
              </Flex>
              {!!syncSummary.archived_users?.length && (
                <Text size="1" color="gray">
                  Archived users: {syncSummary.archived_users.join(", ")}
                </Text>
              )}
            </div>
          )}

          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Harvest User</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>BambooHR Mapping</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Roles</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((user) => {
                const recommended = user.recommended_bamboohr as {
                  id?: string;
                  display_name?: string;
                  strategy?: string;
                  confidence?: number;
                };
                const selectedBamboohrId = user.bamboohr_user_id || recommended.id || "__none__";

                return (
                  <Table.Row
                    key={user.id}
                    className={
                      newlyCreatedUserIds.includes(user.id)
                        ? "bg-green-50/50 dark:bg-green-900/20"
                        : undefined
                    }
                  >
                    <Table.Cell>
                      <Text weight="bold">{user.harvest_name}</Text>
                      <Text as="div" size="1" color="gray">
                        {user.email}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="space-y-1">
                        <Select.Root
                          value={selectedBamboohrId}
                          onValueChange={(value) => {
                            if (value === "__none__") {
                              updateUser(user.id, {
                                bamboohr_user_id: "",
                                bamboohr_display_name: "",
                              });
                              return;
                            }

                            const option = bamboohrOptions.find((item) => item.id === value);
                            updateUser(user.id, {
                              bamboohr_user_id: value,
                              bamboohr_display_name: option?.display_name || "",
                            });
                          }}
                        >
                          <Select.Trigger placeholder="Select BambooHR user" />
                          <Select.Content>
                            <Select.Item value="__none__">Unmapped</Select.Item>
                            {bamboohrOptions.map((option) => (
                              <Select.Item key={option.id} value={option.id}>
                                {option.display_name}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                        {!user.bamboohr_user_id && recommended.id && (
                          <div className="mt-1 flex items-center gap-1.5">
                            <Badge size="1" color="blue" variant="soft">
                              Recommended
                            </Badge>
                            <Text size="1" color="gray">
                              {recommended.display_name} ({recommended.strategy},{" "}
                              {recommended.confidence?.toFixed(1)})
                            </Text>
                          </div>
                        )}
                        {user.bamboohr_user_id && (
                          <div className="mt-1 flex items-center gap-1.5">
                            <Badge size="1" color="green" variant="soft">
                              Mapped
                            </Badge>
                            <Text size="1" color="gray">
                              {mappedDisplayName(user)}
                            </Text>
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Popover.Root>
                        <Popover.Trigger>
                          <Button variant="soft" size="1">
                            {user.roles.map(roleLabel).join(", ") || "Select roles"}
                          </Button>
                        </Popover.Trigger>
                        <Popover.Content size="1">
                          <Flex direction="column" gap="2">
                            {roleValues.map((role) => (
                              <Text as="label" size="2" key={role}>
                                <Flex gap="2" align="center">
                                  <Checkbox
                                    checked={user.roles.includes(role)}
                                    onCheckedChange={() => toggleRole(user.id, user.roles, role)}
                                  />
                                  {roleLabel(role)}
                                </Flex>
                              </Text>
                            ))}
                          </Flex>
                        </Popover.Content>
                      </Popover.Root>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={user.status === "active" ? "green" : "gray"}>
                        {user.status}
                      </Badge>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </div>
      </AppLayout>
    </>
  );
}
